import {
  Camera,
  Euler,
  Event,
  EventDispatcher,
  Group,
  Matrix4,
  MOUSE,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Quaternion,
  Spherical,
  TOUCH,
  Vector2,
  Vector3,
} from 'three'

const _changeEvent = { type: 'change' }
const _startEvent = { type: 'start' }
const _endEvent = { type: 'end' }
const EPS = 0.000001
const polarOffset = (5 * Math.PI) / 12

const STATE = {
  NONE: -1,
  ROTATE: 0,
  DOLLY: 1,
  PAN: 2,
  TOUCH_ROTATE: 3,
  TOUCH_PAN: 4,
  TOUCH_DOLLY_PAN: 5,
  TOUCH_DOLLY_ROTATE: 6,
}
let state = STATE.NONE

type CameraType = PerspectiveCamera | OrthographicCamera

// Temporarily
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/restrict-plus-operands */

export class ModelControls extends EventDispatcher {
  camera: Camera
  object: Object3D | Group | undefined
  domElement: HTMLElement
  enabled: boolean
  visible: boolean

  target: Vector3

  minDistance: number
  maxDistance: number

  minZoom: number
  maxZoom: number

  minPolarAngle: number
  maxPolarAngle: number

  minAzimuthAngle: number
  maxAzimuthAngle: number

  enableDamping: boolean
  dampingFactor: number

  enableZoom: boolean
  zoomSpeed: number

  enableRotate: boolean
  rotateSpeed: number

  enablePan: boolean
  panSpeed: number
  screenSpacePanning: boolean
  keyPanSpeed: number

  autoRotate: boolean
  autoRotateSpeed: number

  keys: { LEFT: string; UP: string; RIGHT: string; BOTTOM: string }
  mouseButtons: Partial<{ LEFT: MOUSE; MIDDLE: MOUSE; RIGHT: MOUSE }>
  touches: Partial<{ ONE: TOUCH; TWO: TOUCH }>

  target0: Vector3
  position0: Vector3
  zoom0: number
  rotation0: Euler | undefined

  getPolarAngle: () => number
  getAzimuthalAngle: () => number
  getDistance: () => number

  private _domElementKeyEvents: HTMLElement | Window | null

  private children: Object3D | undefined

  private spherical: Spherical
  private sphericalDelta: Spherical
  private euler: Euler
  private eulerDelta: Euler
  private scale: number
  private zoomChanged: boolean

  private rotateStart: Vector2
  private rotateEnd: Vector2
  private rotateDelta: Vector2

  private panOffset: Vector3
  private panStart: Vector2
  private panEnd: Vector2
  private panDelta: Vector2

  private dollyStart: Vector2
  private dollyEnd: Vector2
  private dollyDelta: Vector2

  private updateOffset: Vector3
  private updateLastPosition: Vector3
  private updateQuat: Quaternion
  private updateQuatInverse: Quaternion

  private updateLastQuaternionObject: Quaternion

  private panLeftV: Vector3
  private panUpV: Vector3
  private panInternalOffset: THREE.Vector3

  private pointers: Array<Event>
  private pointerPositions: Array<Vector2>

  private onContextMenu: (event: Event) => void
  private onMouseWheel: (event: Event) => void
  private onPointerMove: (event: Event) => void
  private onPointerUp: (event: Event) => void
  private onPointerDown: (event: Event) => void
  private onPointerCancel: (event: Event) => void

  constructor(camera: Camera, domElement: HTMLElement) {
    super()

    this.camera = camera
    this.domElement = domElement
    this.domElement.style.touchAction = 'none'
    this.enabled = true

    this.visible = this.children !== undefined

    this.target = new Vector3()

    this.minDistance = 0
    this.maxDistance = Infinity

    this.minZoom = 0
    this.maxZoom = Infinity

    this.minPolarAngle = 0
    this.maxPolarAngle = Math.PI

    this.minAzimuthAngle = -Infinity
    this.maxAzimuthAngle = Infinity

    this.enableDamping = false
    this.dampingFactor = 0.05

    this.enableZoom = true
    this.zoomSpeed = 1.0

    this.enableRotate = true
    this.rotateSpeed = 1.0

    this.enablePan = true
    this.panSpeed = 1.0
    this.screenSpacePanning = true
    this.keyPanSpeed = 7.0

    this.autoRotate = false
    this.autoRotateSpeed = 2.0

    this.keys = {
      LEFT: 'ArrowLeft',
      UP: 'ArrowUp',
      RIGHT: 'ArrowRight',
      BOTTOM: 'ArrowDown',
    }

    this.mouseButtons = {
      LEFT: MOUSE.ROTATE,
      MIDDLE: MOUSE.DOLLY,
      RIGHT: MOUSE.PAN,
    }

    this.touches = {
      ONE: TOUCH.ROTATE,
      TWO: TOUCH.DOLLY_PAN,
    }

    this.target0 = this.target.clone()
    this.position0 = this.camera.position.clone()
    this.zoom0 = (this.camera as CameraType).zoom
    this.rotation0 = this.object?.rotation

    this._domElementKeyEvents = null

    this.object = new Group()
    this.children = new Object3D()

    this.spherical = new Spherical()
    this.sphericalDelta = new Spherical()
    this.euler = new Euler()
    this.eulerDelta = new Euler()
    this.scale = 1
    this.zoomChanged = false

    this.rotateStart = new Vector2()
    this.rotateEnd = new Vector2()
    this.rotateDelta = new Vector2()

    this.panOffset = new Vector3()
    this.panStart = new Vector2()
    this.panEnd = new Vector2()
    this.panLeftV = new Vector3()
    this.panUpV = new Vector3()
    this.panDelta = new Vector2()
    this.panInternalOffset = new Vector3()

    this.dollyStart = new Vector2()
    this.dollyEnd = new Vector2()
    this.dollyDelta = new Vector2()

    this.updateOffset = new Vector3()
    this.updateLastPosition = new Vector3()
    this.updateQuat = new Quaternion().setFromUnitVectors(this.camera.up, new Vector3(0, 1, 0))
    this.updateQuatInverse = this.updateQuat.clone().invert()

    this.updateLastQuaternionObject = new Quaternion()

    this.pointers = []
    this.pointerPositions = []

    this.getPolarAngle = () => {
      return this.spherical.phi
    }

    this.getAzimuthalAngle = () => {
      return this.spherical.theta
    }

    this.getDistance = () => {
      return this.camera.position.distanceTo(this.target)
    }

    this.onPointerDown = (event: Event) => {
      if (this.enabled === false) {
        return
      }

      if (this.pointers.length === 0) {
        this.domElement.setPointerCapture(event.pointerId as number)
        this.domElement.addEventListener('pointermove', this.onPointerMove)
        this.domElement.addEventListener('pointerup', this.onPointerUp)
      }

      this.addPointer(event)
      if (event.pointerType === 'touch') {
        this.onTouchStart(event)
      } else {
        this.onMouseDown(event)
      }
    }

    this.onPointerMove = (event: Event) => {
      if (this.enabled === false) {
        return
      }

      if (event.pointerType === 'touch') {
        this.onTouchMove(event)
      } else {
        this.onMouseMove(event)
      }
    }

    this.onPointerUp = (event: Event) => {
      this.removePointer(event)

      if (this.pointers.length === 0) {
        this.domElement.releasePointerCapture(event.pointerId as number)
        this.domElement.removeEventListener('pointermove', this.onPointerMove)
        this.domElement.removeEventListener('pointerup', this.onPointerUp)
      }

      this.dispatchEvent(_endEvent)
      state = STATE.NONE
    }

    this.onPointerCancel = (event: Event) => {
      this.removePointer(event)
    }

    this.onMouseWheel = (event: Event) => {
      if (this.enabled === false || this.enableZoom === false || state !== STATE.NONE) {
        return
      }

      event.preventDefault()

      this.dispatchEvent(_startEvent)
      this.handleMouseWheel(event)
      this.dispatchEvent(_endEvent)
    }

    this.onContextMenu = (event: Event) => {
      if (this.enabled === false) {
        return
      }

      event.preventDefault()
    }

    this.domElement.addEventListener('contextmenu', this.onContextMenu)
    this.domElement.addEventListener('pointerdown', this.onPointerDown)
    this.domElement.addEventListener('pointercancel', this.onPointerCancel)
    this.domElement.addEventListener('wheel', this.onMouseWheel, { passive: false })

    this.update()
  }

  update(): boolean | undefined {
    const twoPI = 2 * Math.PI
    const position = this.camera.position
    this.updateOffset.copy(position).sub(this.target)
    this.updateOffset.applyQuaternion(this.updateQuat)
    this.spherical.setFromVector3(this.updateOffset)

    const rotation = this.object?.rotation ?? new Euler()
    this.euler.setFromVector3(new Vector3(rotation.x, rotation.y, rotation.z))

    if (this.autoRotate && state === STATE.NONE) {
      this.rotateLeftEuler(this.getAutoRotationAngle())
    }

    this.spherical.theta += this.sphericalDelta.theta
    this.spherical.phi += this.sphericalDelta.phi

    if (this.enableDamping) {
      this.euler.x -= this.eulerDelta.x * this.dampingFactor
      this.euler.y -= this.eulerDelta.y * this.dampingFactor
    } else {
      this.euler.x -= this.eulerDelta.x
      this.euler.y -= this.eulerDelta.y
    }

    let min = this.minAzimuthAngle
    let max = this.maxAzimuthAngle
    if (isFinite(min) && isFinite(max)) {
      if (min < -Math.PI) {
        min += twoPI
      } else if (min > Math.PI) {
        min -= twoPI
      }

      if (max < -Math.PI) {
        max += twoPI
      } else if (max > Math.PI) {
        max -= twoPI
      }

      if (min <= max) {
        this.euler.y = Math.max(min, Math.min(max, this.euler.y))
      } else {
        this.euler.y =
          this.euler.y > (min + max) / 2 ? Math.max(min, this.euler.y) : Math.min(max, this.euler.y)
      }
    }

    this.euler.x = Math.max(
      this.minPolarAngle - polarOffset + EPS,
      Math.min(this.maxPolarAngle - polarOffset - EPS, this.euler.x),
    )

    this.spherical.radius *= this.scale
    this.spherical.radius = Math.max(
      this.minDistance,
      Math.min(this.maxDistance, this.spherical.radius),
    )

    if (this.enableDamping === true) {
      this.target.addScaledVector(this.panOffset, this.dampingFactor)
    } else {
      this.target.add(this.panOffset)
    }

    this.updateOffset.setFromSpherical(this.spherical)

    if (this.object) {
      this.object.rotation.set(this.euler.x, this.euler.y, this.euler.z, 'ZXY')
    }

    this.updateOffset.applyQuaternion(this.updateQuatInverse)
    position.copy(this.target).add(this.updateOffset)
    this.camera.lookAt(this.target)

    if (this.object && this.children) {
      this.children.position.set(-this.target.x, -this.target.y, -this.target.z)
      this.object.position.set(this.target.x, this.target.y, this.target.z)
    }

    if (this.enableDamping === true) {
      this.eulerDelta.x *= 1 - this.dampingFactor
      this.eulerDelta.y *= 1 - this.dampingFactor
      this.panOffset.multiplyScalar(1 - this.dampingFactor)
    } else {
      this.panOffset.set(0, 0, 0)

      this.eulerDelta.set(0, 0, 0)
    }

    this.sphericalDelta.set(0, 0, 0)

    this.scale = 1

    if (
      this.zoomChanged ||
      this.updateLastPosition.distanceToSquared(this.camera.position) > EPS ||
      (this.object && 8 * (1 - this.updateLastQuaternionObject.dot(this.object?.quaternion)) > EPS)
    ) {
      this.dispatchEvent(_changeEvent)
      this.updateLastPosition.copy(this.camera.position)
      if (this.object) {
        this.updateLastQuaternionObject.copy(this.object?.quaternion)
      }

      this.zoomChanged = false

      return true
    }

    return false
  }

  attach(group: Object3D | Group | undefined): void {
    if (!group || this.object === group) {
      return
    }

    this.object = group
    this.children = group.children[0]
    this.visible = true
  }

  detach(): void {
    this.object = undefined
    this.children = undefined
    this.visible = false
  }

  listenToKeyEvents(domElement: HTMLElement | Window): void {
    domElement.addEventListener('keydown', this.onKeyDown)
    this._domElementKeyEvents = domElement
  }

  saveState(): void {
    this.target0.copy(this.target)
    this.position0.copy(this.camera.position)
    this.zoom0 = (this.camera as CameraType).zoom
    this.rotation0 = this.object?.rotation ?? new Euler()
  }

  reset(): void {
    this.target.copy(this.target0)
    this.camera.position.copy(this.position0)
    this.object?.rotation.copy(this.rotation0 ?? new Euler())
    ;(this.camera as CameraType).zoom = this.zoom0
    ;(this.camera as CameraType).updateProjectionMatrix()
    this.dispatchEvent(_changeEvent)
    this.update()
    state = STATE.NONE
  }

  dispose(): void {
    this.domElement.removeEventListener('contextmenu', this.onContextMenu, false)
    this.domElement.removeEventListener('pointerdown', this.onPointerDown, false)
    this.domElement.removeEventListener('pointercancel', this.onPointerCancel, false)
    this.domElement.removeEventListener('wheel', this.onMouseWheel, false)
    this.domElement.removeEventListener('pointermove', this.onPointerMove, false)
    this.domElement.removeEventListener('pointerup', this.onPointerUp, false)

    if (this._domElementKeyEvents !== null) {
      this._domElementKeyEvents.removeEventListener('keydown', this.onKeyDown)
    }

    this.detach()
  }

  private rotateLeftEuler(angle: number): void {
    this.eulerDelta.y -= angle
  }

  private rotateUpEuler(angle: number): void {
    this.eulerDelta.x -= angle
  }

  private getAutoRotationAngle(): number {
    return ((2 * Math.PI) / 60 / 60) * this.autoRotateSpeed
  }

  private getZoomScale(): number {
    return Math.pow(0.95, this.zoomSpeed)
  }

  private onMouseMove(event: Event): void {
    switch (state) {
      case STATE.ROTATE: {
        if (this.enableRotate === false) {
          return
        }

        this.handleMouseMoveRotate(event)

        break
      }
      case STATE.DOLLY: {
        if (this.enableZoom === false) {
          return
        }

        this.handleMouseMoveDolly(event)

        break
      }
      case STATE.PAN: {
        if (this.enablePan === false) {
          return
        }

        this.handleMouseMovePan(event)

        break
      }
    }
  }

  private onTouchMove(event: Event): void {
    this.trackPointer(event)

    switch (state) {
      case STATE.TOUCH_ROTATE: {
        if (this.enableRotate === false) {
          return
        }

        this.handleTouchMoveRotate(event)
        this.update()

        break
      }
      case STATE.TOUCH_PAN: {
        if (this.enablePan === false) {
          return
        }

        this.handleTouchMovePan(event)
        this.update()

        break
      }
      case STATE.TOUCH_DOLLY_PAN: {
        if (this.enableZoom === false && this.enablePan === false) {
          return
        }

        this.handleTouchMoveDollyPan(event)
        this.update()

        break
      }
      case STATE.TOUCH_DOLLY_ROTATE: {
        if (this.enableZoom === false && this.enableRotate === false) {
          return
        }

        this.handleTouchMoveDollyRotate(event)
        this.update()

        break
      }
      default:
        state = STATE.NONE
    }
  }

  private onMouseDown(event: Event): void {
    let mouseAction

    switch (event.button) {
      case 0: {
        mouseAction = this.mouseButtons.LEFT

        break
      }
      case 1: {
        mouseAction = this.mouseButtons.MIDDLE

        break
      }
      case 2: {
        mouseAction = this.mouseButtons.RIGHT

        break
      }
      default:
        mouseAction = -1
    }

    switch (mouseAction) {
      case MOUSE.DOLLY: {
        if (this.enableZoom === false) {
          return
        }

        this.handleMouseDownDolly(event)
        state = STATE.DOLLY

        break
      }

      case MOUSE.ROTATE: {
        if (event.ctlKey || event.metaKey || event.shiftKey) {
          if (this.enablePan === false) {
            return
          }

          this.handleMouseDownPan(event)
          state = STATE.PAN
        } else {
          if (this.enableRotate === false) {
            return
          }

          this.handleMouseDownRotate(event)
          state = STATE.ROTATE
        }

        break
      }

      case MOUSE.PAN: {
        if (event.ctlKey || event.metaKey || event.shiftKey) {
          if (this.enableRotate === false) {
            return
          }

          this.handleMouseDownRotate(event)
          state = STATE.ROTATE
        } else {
          if (this.enablePan === false) {
            return
          }

          this.handleMouseDownPan(event)
          state = STATE.PAN
        }

        break
      }

      default:
        state = STATE.NONE
    }

    if (state !== STATE.NONE) {
      this.dispatchEvent(_startEvent)
    }
  }

  private onTouchStart(event: Event): void {
    this.trackPointer(event)

    switch (this.pointers.length) {
      case 1: {
        switch (this.touches.ONE) {
          case TOUCH.ROTATE:
            if (this.enableRotate === false) {
              return
            }

            this.handleTouchStartRotate()
            state = STATE.TOUCH_ROTATE

            break
          case TOUCH.PAN:
            if (this.enablePan === false) {
              return
            }

            this.handleTouchStartPan()
            state = STATE.TOUCH_PAN

            break
          default:
            state = STATE.NONE
        }

        break
      }

      case 2: {
        switch (this.touches.TWO) {
          case TOUCH.DOLLY_PAN: {
            if (this.enableZoom === false && this.enablePan === false) {
              return
            }

            this.handleTouchStartDollyPan()
            state = STATE.TOUCH_DOLLY_PAN

            break
          }

          case TOUCH.DOLLY_ROTATE: {
            if (this.enableZoom === false && this.enableRotate === false) {
              return
            }

            this.handleTouchStartDollyRotate()
            state = STATE.TOUCH_DOLLY_ROTATE

            break
          }
          default:
            state = STATE.NONE
        }

        break
      }

      default:
        state = STATE.NONE
    }

    if (state !== STATE.NONE) {
      this.dispatchEvent(_startEvent)
    }
  }

  private trackPointer(event: Event): void {
    let position = this.pointerPositions[event.pointerId as number]
    if (position === undefined) {
      position = new Vector2()
      this.pointerPositions[event.pointerId as number] = position
    }

    position.set(event.pageX as number, event.pageY as number)
  }

  private handleMouseMoveRotate(event: Event): void {
    this.rotateEnd.set(event.clientX as number, event.clientY as number)
    this.rotateDelta.subVectors(this.rotateEnd, this.rotateStart).multiplyScalar(this.rotateSpeed)

    const element = this.domElement
    const twoPI = 2 * Math.PI
    this.rotateLeftEuler((twoPI * this.rotateDelta.x) / element.clientHeight)
    this.rotateUpEuler((twoPI * this.rotateDelta.y) / element.clientHeight)

    this.rotateStart.copy(this.rotateEnd)

    this.update()
  }

  private handleMouseDownPan(event: Event): void {
    this.panStart.set(event.clientX as number, event.clientY as number)
  }

  private handleMouseDownRotate(event: Event): void {
    this.rotateStart.set(event.clientX as number, event.clientY as number)
  }

  private handleMouseDownDolly(event: Event): void {
    this.dollyStart.set(event.clientX as number, event.clientY as number)
  }

  private handleTouchStartRotate(): void {
    if (this.pointers.length === 1) {
      this.rotateStart.set(this.pointers[0].pageX as number, this.pointers[0].pageY as number)
    } else {
      const x = 0.5 * (this.pointers[0].pageX + this.pointers[1].pageX)
      const y = 0.5 * (this.pointers[0].pageY + this.pointers[1].pageY)

      this.rotateStart.set(x, y)
    }
  }

  private handleTouchStartPan(): void {
    if (this.pointers.length === 1) {
      this.panStart.set(this.pointers[0].pageX as number, this.pointers[0].pageY as number)
    } else {
      const x = 0.5 * (this.pointers[0].pageX + this.pointers[1].pageX)
      const y = 0.5 * (this.pointers[0].pageY + this.pointers[1].pageY)

      this.panStart.set(x, y)
    }
  }

  private handleTouchStartDollyPan(): void {
    if (this.enableZoom) {
      this.handleTouchStartDolly()
    }

    if (this.enablePan) {
      this.handleTouchStartPan()
    }
  }

  private handleTouchStartDollyRotate(): void {
    if (this.enableZoom) {
      this.handleTouchStartDolly()
    }

    if (this.enableRotate) {
      this.handleTouchStartRotate()
    }
  }

  private handleTouchStartDolly(): void {
    const dx = this.pointers[0].pageX - this.pointers[1].pageX
    const dy = this.pointers[0].pageY - this.pointers[1].pageY

    const distance = Math.sqrt(dx * dx + dy * dy)

    this.dollyStart.set(0, distance)
  }

  private handleTouchMoveRotate(event: Event): void {
    if (this.pointers.length === 1) {
      this.rotateEnd.set(event.pageX as number, event.pageY as number)
    } else {
      const position = this.getSecondPointerPosition(event)

      const x = 0.5 * (event.pageX + position.x)
      const y = 0.5 * (event.pageY + position.y)

      this.rotateEnd.set(x, y)
    }

    this.rotateDelta.subVectors(this.rotateEnd, this.rotateStart).multiplyScalar(this.rotateSpeed)
    const element = this.domElement

    this.rotateLeftEuler(this.rotateDelta.x / element.clientHeight)
    this.rotateUpEuler(this.rotateDelta.y / element.clientHeight)

    this.rotateStart.copy(this.rotateEnd)
  }

  private handleTouchMovePan(event: Event): void {
    if (this.pointers.length === 1) {
      this.panEnd.set(event.pageX as number, event.pageY as number)
    } else {
      const position = this.getSecondPointerPosition(event)

      const x = 0.5 * (event.pageX + position.x)
      const y = 0.5 * (event.pageY + position.y)

      this.panEnd.set(x, y)
    }

    this.panDelta.subVectors(this.panEnd, this.panStart).multiplyScalar(this.panSpeed)
    this.pan(this.panDelta.x, this.panDelta.y)
    this.panStart.copy(this.panEnd)
  }

  private handleMouseMovePan(event: Event): void {
    this.panEnd.set(event.clientX as number, event.clientY as number)
    this.panDelta.subVectors(this.panEnd, this.panStart).multiplyScalar(this.panSpeed)
    this.pan(this.panDelta.x, this.panDelta.y)
    this.panStart.copy(this.panEnd)
    this.update()
  }

  private getSecondPointerPosition(event: Event): Vector2 {
    const pointer =
      event.pointerId === this.pointers[0].pointerId ? this.pointers[1] : this.pointers[0]
    return this.pointerPositions[pointer.pointerId as number]
  }

  private handleMouseMoveDolly(event: Event): void {
    this.dollyEnd.set(event.clientX as number, event.clientY as number)
    this.dollyDelta.subVectors(this.dollyEnd, this.dollyStart)

    if (this.dollyDelta.y > 0) {
      this.dollyOut(this.getZoomScale())
    } else if (this.dollyDelta.y < 0) {
      this.dollyIn(this.getZoomScale())
    }

    this.dollyStart.copy(this.dollyEnd)
    this.update()
  }

  private handleTouchMoveDollyPan(event: Event): void {
    if (this.enableZoom) {
      this.handleTouchMoveDolly(event)
    }

    if (this.enablePan) {
      this.handleTouchMovePan(event)
    }
  }

  private handleTouchMoveDollyRotate(event: Event): void {
    if (this.enableZoom) {
      this.handleTouchMoveDolly(event)
    }

    if (this.enableRotate) {
      this.handleTouchMoveRotate(event)
    }
  }

  private handleTouchMoveDolly(event: Event): void {
    const position = this.getSecondPointerPosition(event)

    const dx = event.pageX - position.x
    const dy = event.pageY - position.y

    const distance = Math.sqrt(dx * dx + dy * dy)
    this.dollyEnd.set(0, distance)
    this.dollyDelta.set(0, Math.pow(this.dollyEnd.y / this.dollyStart.y, this.zoomSpeed))
    this.dollyOut(this.dollyDelta.y)
    this.dollyStart.copy(this.dollyEnd)
  }

  private handleMouseWheel(event: Event): void {
    if (event.deltaY < 0) {
      this.dollyIn(this.getZoomScale())
    } else if (event.deltaY > 0) {
      this.dollyOut(this.getZoomScale())
    }

    this.update()
  }

  private dollyOut(dollyScale: number): void {
    if ((this.camera as PerspectiveCamera).isPerspectiveCamera) {
      this.scale /= dollyScale
    } else if ((this.camera as OrthographicCamera).isOrthographicCamera) {
      ;(this.camera as CameraType).zoom = Math.max(
        this.minZoom,
        Math.min(this.maxZoom, (this.camera as CameraType).zoom * dollyScale),
      )
      ;(this.camera as CameraType).updateProjectionMatrix()
      this.zoomChanged = true
    } else {
      console.warn(
        'WARNING: ModelControls encountered an unknown camera type - dolly/zoom disabled.',
      )
      this.enableZoom = false
    }
  }

  private dollyIn(dollyScale: number): void {
    if ((this.camera as PerspectiveCamera).isPerspectiveCamera) {
      this.scale *= dollyScale
    } else if ((this.camera as OrthographicCamera).isOrthographicCamera) {
      ;(this.camera as CameraType).zoom = Math.max(
        this.minZoom,
        Math.min(this.maxZoom, (this.camera as OrthographicCamera).zoom / dollyScale),
      )
      ;(this.camera as OrthographicCamera).updateProjectionMatrix()
      this.zoomChanged = true
    } else {
      console.warn(
        'WARNING: ModelControls encountered an unknown camera type - dolly/zoom disabled',
      )
      this.enableZoom = false
    }
  }

  private addPointer(event: Event): void {
    this.pointers.push(event)
  }

  private removePointer(event: Event): void {
    delete this.pointerPositions[event.pointerId as number]

    for (let i = 0; i < this.pointers.length; i++) {
      if (this.pointers[i].pointerId === event.pointerId) {
        this.pointers.splice(i, 1)

        return
      }
    }
  }

  private onKeyDown(event: Event): void {
    if (this.enabled === false || this.enablePan === false) {
      return
    }

    this.handleKeyDown(event)
  }

  private handleKeyDown(event: Event): void {
    let needsUpdate = false

    switch (event.code) {
      case this.keys.UP: {
        this.pan(0, this.keyPanSpeed)
        needsUpdate = true

        break
      }
      case this.keys.BOTTOM: {
        this.pan(0, -this.keyPanSpeed)
        needsUpdate = true

        break
      }
      case this.keys.LEFT: {
        this.pan(this.keyPanSpeed, 0)
        needsUpdate = true

        break
      }
      case this.keys.RIGHT: {
        this.pan(-this.keyPanSpeed, 0)
        needsUpdate = true

        break
      }
    }

    if (needsUpdate) {
      event.preventDefault()
      this.update()
    }
  }

  private pan(deltaX: number, deltaY: number): void {
    const element = this.domElement
    if ((this.camera as PerspectiveCamera).isPerspectiveCamera) {
      const position = this.camera.position
      this.panInternalOffset.copy(position).sub(this.target)
      let targetDistance = this.panInternalOffset.length()

      targetDistance *= Math.tan((((this.camera as PerspectiveCamera).fov / 2) * Math.PI) / 180.0)

      this.panLeft((2 * deltaX * targetDistance) / element.clientHeight, this.camera.matrix)
      this.panUp((2 * deltaY * targetDistance) / element.clientHeight, this.camera.matrix)
    } else if ((this.camera as OrthographicCamera).isOrthographicCamera) {
      this.panLeft(
        (deltaX *
          ((this.camera as OrthographicCamera).right - (this.camera as OrthographicCamera).left)) /
          (this.camera as OrthographicCamera).zoom /
          element.clientWidth,
        this.camera.matrix,
      )
      this.panUp(
        (deltaY *
          ((this.camera as OrthographicCamera).top - (this.camera as OrthographicCamera).bottom)) /
          (this.camera as OrthographicCamera).zoom /
          element.clientHeight,
        this.camera.matrix,
      )
    } else {
      console.warn('WARNING: ModelControls encountered an unknown camera type - pan disabled.')
      this.enablePan = false
    }
  }

  private panLeft(distance: number, objectMatrix: Matrix4): void {
    this.panLeftV.setFromMatrixColumn(objectMatrix, 0)
    this.panLeftV.multiplyScalar(-distance)
    this.panOffset.add(this.panLeftV)
  }

  private panUp(distance: number, objectMatrix: Matrix4): void {
    if (this.screenSpacePanning === true) {
      this.panUpV.setFromMatrixColumn(objectMatrix, 1)
    } else {
      this.panUpV.setFromMatrixColumn(objectMatrix, 0)
      this.panUpV.crossVectors(this.camera.up, this.panUpV)
    }

    this.panUpV.multiplyScalar(distance)
    this.panOffset.add(this.panUpV)
  }
}
